// All time fields in this slice are integer milliseconds.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config';

// --- Async Thunks ---

export const fetchTimer = createAsyncThunk(
    'timer/fetchTimer',
    async ({ cardId }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/cards/${cardId}/timer`, {
                method: 'GET',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to fetch timer (${res.status})`);
            }

            return { cardId, data: await res.json() };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

export const startTimer = createAsyncThunk(
    'timer/startTimer',
    async ({ cardId, mode, duration, elapsed }, { rejectWithValue }) => {
        const startTime = new Date();
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/cards/${cardId}/timer/start`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode,
                    duration: mode === 'countdown' ? duration : undefined,
                    elapsed,
                    startTime
                }),
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to start timer (${res.status})`);
            }

            return { cardId, data: await res.json() };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);
export const resumeTimer = createAsyncThunk(
    'timer/resumeTimer',
    async ({ cardId, mode, duration, elapsed }, { rejectWithValue }) => {
        const startTime = new Date();
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/cards/${cardId}/timer/resume`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode,
                    duration: mode === 'countdown' ? duration : undefined,
                    elapsed,
                    startTime
                }),
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to start timer (${res.status})`);
            }

            return { cardId, data: await res.json() };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

export const syncTimerAction = createAsyncThunk(
    'timer/syncTimerAction',
    async ({ cardId, action, mode, duration, elapsed, clickMs }, { rejectWithValue }) => {
        // Prefer the caller-supplied clickMs (captured at the UI event) so the
        // server anchors its calculation to the exact click moment, not to when
        // the thunk happened to execute. Falls back to now for old callers.
        const actionTime = new Date(Number.isFinite(clickMs) ? clickMs : Date.now());
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/cards/${cardId}/timer/${action}`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode,
                    duration: mode === 'countdown' ? duration : undefined,
                    elapsed,
                    actionTime
                }),
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to ${action} timer (${res.status})`);
            }

            const data = await res.json();
            if (data.status === 'error') throw new Error(`Server error on ${action}`);
            return { cardId, data };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

export const switchTimerMode = createAsyncThunk(
    'timer/switchTimerMode',
    async ({ cardId, mode }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/cards/${cardId}/timer/mode`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ mode }),
            });
            if (!res.ok) {
                const errorData = await res.json();
                throw new Error(errorData.message || `Failed to switch timer mode`);
            }
            const data = await res.json();
            return { cardId, data };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

export const fetchRunningTimers = createAsyncThunk(
    'timer/fetchRunningTimers',
    async (_, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/status`, {
                credentials: 'include',
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to fetch running timers (${res.status})`);
            }

            const data = await res.json();

            return data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// New optimized timer operations using the Timer model
export const createOrUpdateTimer = createAsyncThunk(
    'timer/createOrUpdateTimer',
    async ({ cardId, mode, status, duration, stopwatchElapsed, countdownElapsed }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/timers`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cardId,
                    mode,
                    status,
                    duration,
                    stopwatchElapsed,
                    countdownElapsed
                }),
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to update timer (${res.status})`);
            }

            return { cardId, data: await res.json() };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

export const fetchTimerByCard = createAsyncThunk(
    'timer/fetchTimerByCard',
    async ({ cardId }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/card/${cardId}`, {
                credentials: 'include',
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to fetch timer (${res.status})`);
            }

            return { cardId, data: await res.json() };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

export const fetchTimerBreakdown = createAsyncThunk(
    'timer/fetchTimerBreakdown',
    async ({ cardId, startDate, endDate }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams();
            if (startDate) params.set('startDate', startDate);
            if (endDate) params.set('endDate', endDate);
            const res = await fetch(`${config.API_URI}/v1/timers/cards/${cardId}/timer/sessions/breakdown?${params}`, {
                credentials: 'include'
            });
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to fetch breakdown (${res.status})`);
            }
            return { cardId, data: await res.json() };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

export const deleteTimer = createAsyncThunk(
    'timer/deleteTimer',
    async ({ cardId }, { rejectWithValue }) => {
        try {
            const res = await fetch(`${config.API_URI}/v1/timers/card/${cardId}`, {
                method: 'DELETE',
                credentials: 'include',
            });

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || `Failed to delete timer (${res.status})`);
            }

            return { cardId };
        } catch (error) {
            return rejectWithValue({ cardId, error: error.message });
        }
    }
);

// --- Helpers ---

const createDefaultTimerState = () => ({
    mode: 'stopwatch',
    timeLeft: 0,
    isRunning: false,
    totalTime: 0,
    timerStatus: 'idle',
    startTime: null,
    stopwatchElapsed: 0,
    countdownElapsed: 0,
    title: null,
    error: null,
    hydrated: false,
});

// --- Initial State ---

// Load timer manager state from localStorage
const loadTimerManagerState = () => {
    try {
        const saved = localStorage.getItem('timerManagerState');
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                isVisible: parsed.isVisible ?? true, // Default to true if not set
                isCollapsed: parsed.isCollapsed ?? false,
                position: parsed.position ?? { x: window.innerWidth - 320, y: 20 },
                isDragging: false,
                hasBeenManuallyHidden: parsed.hasBeenManuallyHidden ?? false,
                hasStoredState: true, // Flag to know we loaded from storage
            };
        }
    } catch (error) {
        console.warn('Failed to load timer manager state from localStorage:', error);
    }
    // First time - default to visible
    return {
        isVisible: true,
        isCollapsed: false,
        position: { x: window.innerWidth - 320, y: 20 },
        isDragging: false,
        hasBeenManuallyHidden: false,
        hasStoredState: false, // First time
    };
};

// Save timer manager state to localStorage
const saveTimerManagerState = (timerManager) => {
    try {
        const stateToSave = {
            isVisible: timerManager.isVisible,
            isCollapsed: timerManager.isCollapsed,
            position: timerManager.position,
            hasBeenManuallyHidden: timerManager.hasBeenManuallyHidden,
        };
        localStorage.setItem('timerManagerState', JSON.stringify(stateToSave));
    } catch (error) {
        console.warn('Failed to save timer manager state to localStorage:', error);
    }
};

const initialState = {
    timers: {},
    timerBreakdowns: {},
    loading: false,
    error: null,
    // Timer Manager State
    timerManager: loadTimerManagerState(),
};

// --- Slice ---

const timerSlice = createSlice({
    name: 'timer',
    initialState,
    reducers: {
        setTimer(state, action) {
            const { cardId, timerData } = action.payload;
            const timer = state.timers[cardId] ?? createDefaultTimerState();

            const {
                mode = 'stopwatch',
                status = 'idle',
                startTime = null,
                duration = 0,
                stopwatchElapsed = 0,
                countdownElapsed = 0,
                title = null,
            } = timerData;

            const now = Date.now();
            const start = startTime ? new Date(startTime).getTime() : null;

            timer.mode = mode;
            timer.timerStatus = status;
            timer.totalTime = duration;
            timer.startTime = start;
            timer.stopwatchElapsed = stopwatchElapsed;
            timer.countdownElapsed = countdownElapsed;
            // elapsedOffset is the base the tick loop adds to sessionElapsedMs —
            // without it, reloading a previously-paused-then-resumed timer drops
            // the accumulated base on the first tick.
            timer.elapsedOffset = mode === 'stopwatch' ? stopwatchElapsed : countdownElapsed;
            // Update title if provided
            if (title) {
                timer.title = title;
            }

            // Calculate timeLeft based on mode with proper data isolation
            if (mode === 'stopwatch') {
                if (status === 'running' && start) {
                    const diffMs = Math.max(0, now - start);
                    timer.timeLeft = diffMs + stopwatchElapsed;
                    timer.isRunning = true;
                } else if (status === 'paused') {
                    timer.timeLeft = stopwatchElapsed;
                    timer.isRunning = false;
                } else {
                    // For 'stopped' and 'idle' status, show 0 for fresh start
                    timer.timeLeft = status === 'stopped' ? 0 : stopwatchElapsed;
                    timer.isRunning = false;
                }
                // Ensure countdown data is preserved but not used for display
                timer.countdownElapsed = countdownElapsed;
            }

            if (mode === 'countdown') {
                if (status === 'running' && start) {
                    const diffMs = Math.max(0, now - start);
                    const remaining = duration - (diffMs + countdownElapsed);
                    timer.timeLeft = Math.max(0, remaining);
                    timer.isRunning = remaining > 0;
                    if (remaining <= 0) timer.timerStatus = 'stopped';
                } else if (status === 'paused') {
                    timer.timeLeft = Math.max(0, duration - countdownElapsed);
                    timer.isRunning = false;
                } else if (status === 'stopped') {
                    // When stopped, show 0
                    timer.timeLeft = 0;
                    timer.isRunning = false;
                } else {
                    // For 'idle' status, show full duration if set, otherwise 0
                    timer.timeLeft = duration > 0 ? duration : 0;
                    timer.isRunning = false;
                }
                // Ensure stopwatch data is preserved but not used for display
                timer.stopwatchElapsed = stopwatchElapsed;
            }

            timer.hydrated = true;
            state.timers[cardId] = timer;
        },

        setMode(state, action) {
            const { cardId, mode } = action.payload;
            const timer = state.timers[cardId] ?? createDefaultTimerState();

            // Prevent mode switching when timer is running
            if (timer.isRunning || timer.timerStatus === 'running') {
                timer.error = 'Cannot change timer mode while running. Stop, pause, or reset the timer first.';
                state.timers[cardId] = timer;
                return;
            }

            // Only switch mode if timer is not running
            if (timer.timerStatus === 'idle' || timer.timerStatus === 'stopped' || timer.timerStatus === 'paused') {
                timer.mode = mode;
                timer.error = null;

                // Set timeLeft based on the mode's stored elapsed time
                if (mode === 'stopwatch') {
                    // If timer was stopped, show 0 for fresh start, otherwise show elapsed
                    timer.timeLeft = timer.timerStatus === 'stopped' ? 0 : (timer.stopwatchElapsed || 0);
                    timer.totalTime = 0; // Stopwatch doesn't have a preset total time
                } else if (mode === 'countdown') {
                    // If timer was stopped, show 0 for fresh start, otherwise show remaining time
                    if (timer.timerStatus === 'stopped') {
                        timer.timeLeft = 0;
                    } else {
                        timer.timeLeft = Math.max(0, (timer.totalTime || 0) - (timer.countdownElapsed || 0));
                    }
                }
            }

            state.timers[cardId] = timer;
        },

        setTimeLeft(state, action) {
            const { cardId, timeLeft } = action.payload;
            if (state.timers[cardId]) state.timers[cardId].timeLeft = timeLeft;
        },

        setIsRunning(state, action) {
            const { cardId, isRunning } = action.payload;
            if (state.timers[cardId]) state.timers[cardId].isRunning = isRunning;
        },

        setTimerStatus(state, action) {
            const { cardId, status } = action.payload;
            if (state.timers[cardId]) state.timers[cardId].timerStatus = status;
        },

        setTotalTime(state, action) {
            const { cardId, totalTime } = action.payload;
            if (state.timers[cardId]) state.timers[cardId].totalTime = totalTime;
        },

        setStartTime(state, action) {
            const { cardId, startTime } = action.payload;
            if (state.timers[cardId]) state.timers[cardId].startTime = startTime;
        },

        setElapsedOffset(state, action) {
            const { cardId, elapsedOffset, mode } = action.payload;
            if (!state.timers[cardId]) return;
            if (mode === 'stopwatch') state.timers[cardId].stopwatchElapsed = elapsedOffset;
            if (mode === 'countdown') state.timers[cardId].countdownElapsed = elapsedOffset;
        },

        clearError(state, action) {
            const { cardId } = action.payload;
            if (state.timers[cardId]) state.timers[cardId].error = null;
        },

        setError(state, action) {
            const { cardId, error } = action.payload;
            if (state.timers[cardId]) state.timers[cardId].error = error;
        },

        // Timer Manager Actions
        showTimerManager(state) {
            state.timerManager.isVisible = true;
            state.timerManager.hasBeenManuallyHidden = false;
            saveTimerManagerState(state.timerManager);
        },

        hideTimerManager(state) {
            state.timerManager.isVisible = false;
            state.timerManager.hasBeenManuallyHidden = true;
            saveTimerManagerState(state.timerManager);
        },

        toggleTimerManager(state) {
            state.timerManager.isVisible = !state.timerManager.isVisible;
            if (!state.timerManager.isVisible) {
                state.timerManager.hasBeenManuallyHidden = true;
            } else {
                state.timerManager.hasBeenManuallyHidden = false;
            }
            saveTimerManagerState(state.timerManager);
        },

        collapseTimerManager(state) {
            state.timerManager.isCollapsed = true;
            saveTimerManagerState(state.timerManager);
        },

        expandTimerManager(state) {
            state.timerManager.isCollapsed = false;
            saveTimerManagerState(state.timerManager);
        },

        toggleTimerManagerCollapse(state) {
            state.timerManager.isCollapsed = !state.timerManager.isCollapsed;
            saveTimerManagerState(state.timerManager);
        },

        setTimerManagerPosition(state, action) {
            const { x, y } = action.payload;
            state.timerManager.position = { x, y };
            saveTimerManagerState(state.timerManager);
        },

        setTimerManagerDragging(state, action) {
            state.timerManager.isDragging = action.payload;
            // Don't save dragging state to localStorage
        },

        resetTimerManagerHiddenState(state) {
            state.timerManager.hasBeenManuallyHidden = false;
            saveTimerManagerState(state.timerManager);
        },
    },

    extraReducers: (builder) => {
        builder
            .addCase(fetchTimer.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchTimer.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                timerSlice.caseReducers.setTimer(state, {
                    payload: {
                        cardId,
                        timerData: {
                            mode: data.mode,
                            status: data.status,
                            startTime: data.startTime,
                            duration: data.duration,
                            stopwatchElapsed: data.stopwatchElapsed,
                            countdownElapsed: data.countdownElapsed,
                        },
                    },
                });
                state.loading = false;
            })
            .addCase(fetchTimer.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                state.timers[cardId] = {
                    ...createDefaultTimerState(),
                    error,
                    hydrated: true,
                };
                state.loading = false;
            })
            .addCase(startTimer.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();
                timer.mode = data.mode;
                timer.timerStatus = data.status;
                timer.isRunning = data.status === 'running';
                timer.startTime = data.startTime ? new Date(data.startTime).getTime() : null;
                timer.totalTime = data.duration || 0;
                timer.stopwatchElapsed = data.stopwatchElapsed || 0;
                timer.countdownElapsed = data.countdownElapsed || 0;
                // Update title if provided
                if (data.title) {
                    timer.title = data.title;
                }

                // Set timeLeft based on mode and current state
                if (data.mode === 'countdown') {
                    timer.timeLeft = data.duration ? Math.max(0, data.duration - (data.countdownElapsed || 0)) : 0;
                } else {
                    timer.timeLeft = data.stopwatchElapsed || 0;
                }

                timer.elapsedOffset =
                    data.mode === 'stopwatch'
                        ? data.stopwatchElapsed || 0
                        : data.countdownElapsed || 0;

                timer.hydrated = true;
                state.timers[cardId] = timer;
            })
            .addCase(startTimer.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                state.timers[cardId] = {
                    ...createDefaultTimerState(),
                    error,
                };
            })
            .addCase(resumeTimer.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();
                timer.mode = data.mode;
                timer.timerStatus = data.status;
                timer.isRunning = data.status === 'running';
                timer.startTime = data.startTime ? new Date(data.startTime).getTime() : null;
                timer.totalTime = data.duration || 0;
                timer.stopwatchElapsed = data.stopwatchElapsed || 0;
                timer.countdownElapsed = data.countdownElapsed || 0;
                // Update title if provided
                if (data.title) {
                    timer.title = data.title;
                }

                // Set timeLeft based on mode and current state
                if (data.mode === 'countdown') {
                    timer.timeLeft = data.duration ? Math.max(0, data.duration - (data.countdownElapsed || 0)) : 0;
                } else {
                    timer.timeLeft = data.stopwatchElapsed || 0;
                }

                timer.elapsedOffset =
                    data.mode === 'stopwatch'
                        ? data.stopwatchElapsed || 0
                        : data.countdownElapsed || 0;

                timer.hydrated = true;
                state.timers[cardId] = timer;
            })
            .addCase(resumeTimer.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                state.timers[cardId] = {
                    ...createDefaultTimerState(),
                    error,
                };
            })
            .addCase(syncTimerAction.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();

                timer.mode = data.mode;
                timer.timerStatus = data.status;
                timer.isRunning = data.status === 'running';
                timer.startTime = data.startTime ? new Date(data.startTime).getTime() : null;
                timer.totalTime = data.duration || 0;
                timer.stopwatchElapsed = data.stopwatchElapsed || 0;
                timer.countdownElapsed = data.countdownElapsed || 0;
                // Update title if provided
                if (data.title) {
                    timer.title = data.title;
                }
                // Set timeLeft based on mode and current state
                if (data.mode === 'countdown') {
                    timer.timeLeft = data.duration ? Math.max(0, data.duration - (data.countdownElapsed || 0)) : 0;
                } else {
                    timer.timeLeft = data.stopwatchElapsed || 0;
                }

                timer.elapsedOffset =
                    data.mode === 'stopwatch'
                        ? data.stopwatchElapsed || 0
                        : data.countdownElapsed || 0;

                timer.hydrated = true;
                timer.error = null;

                state.timers[cardId] = timer;
            })
            .addCase(syncTimerAction.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();
                timer.error = error;
                state.timers[cardId] = timer;
            })
            .addCase(switchTimerMode.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                timerSlice.caseReducers.setTimer(state, {
                    payload: {
                        cardId,
                        timerData: {
                            mode: data.mode,
                            status: data.status,
                            startTime: data.startTime,
                            duration: data.duration,
                            stopwatchElapsed: data.stopwatchElapsed,
                            countdownElapsed: data.countdownElapsed,
                            title: data.title,
                        },
                    },
                });
            })
            .addCase(switchTimerMode.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();
                timer.error = error;
                state.timers[cardId] = timer;
            })
            .addCase(fetchRunningTimers.fulfilled, (state, action) => {
                const { runningTimers } = action.payload;
                
                // Clear existing timers first
                state.timers = {};

                // Add running timers to state
                runningTimers.forEach(timerData => {
                    const { cardId, timer, title } = timerData;
                    state.timers[cardId] = {
                        ...createDefaultTimerState(),
                        mode: timer.mode,
                        isRunning: timer.status === 'running',
                        timerStatus: timer.status,
                        startTime: timer.startTime ? new Date(timer.startTime).getTime() : null,
                        totalTime: timer.duration || 0,
                        stopwatchElapsed: timer.stopwatchElapsed || 0,
                        countdownElapsed: timer.countdownElapsed || 0,
                        timeLeft: timer.mode === 'countdown' ? timer.remainingTime || 0 : timer.currentElapsed || 0,
                        elapsedOffset: timer.mode === 'stopwatch' ? timer.stopwatchElapsed || 0 : timer.countdownElapsed || 0,
                        title: title || 'Unknown Card', // Add title to timer state
                        hydrated: true
                    };
                });

                // Don't auto-show timer manager - respect user's saved preference
            })
            .addCase(createOrUpdateTimer.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();

                if (data.timer) {
                    timer.mode = data.timer.mode;
                    timer.timerStatus = data.timer.status;
                    timer.isRunning = data.timer.status === 'running';
                    timer.startTime = data.timer.startTime ? new Date(data.timer.startTime).getTime() : null;
                    timer.totalTime = data.timer.duration || 0;
                    timer.stopwatchElapsed = data.timer.stopwatchElapsed || 0;
                    timer.countdownElapsed = data.timer.countdownElapsed || 0;
                    timer.timeLeft = data.timer.mode === 'countdown' ? data.timer.remainingTime || 0 : data.timer.currentElapsed || 0;
                    timer.elapsedOffset = data.timer.mode === 'stopwatch' ? data.timer.stopwatchElapsed || 0 : data.timer.countdownElapsed || 0;
                    timer.hydrated = true;
                    timer.error = null;
                }

                state.timers[cardId] = timer;
            })
            .addCase(createOrUpdateTimer.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();
                timer.error = error;
                state.timers[cardId] = timer;
            })
            .addCase(fetchTimerByCard.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();

                if (data.timer) {
                    timer.mode = data.timer.mode;
                    timer.timerStatus = data.timer.status;
                    timer.isRunning = data.timer.status === 'running';
                    timer.startTime = data.timer.startTime ? new Date(data.timer.startTime).getTime() : null;
                    timer.totalTime = data.timer.duration || 0;
                    timer.stopwatchElapsed = data.timer.stopwatchElapsed || 0;
                    timer.countdownElapsed = data.timer.countdownElapsed || 0;
                    timer.timeLeft = data.timer.mode === 'countdown' ? data.timer.remainingTime || 0 : data.timer.currentElapsed || 0;
                    timer.elapsedOffset = data.timer.mode === 'stopwatch' ? data.timer.stopwatchElapsed || 0 : data.timer.countdownElapsed || 0;
                    timer.hydrated = true;
                    timer.error = null;
                }

                state.timers[cardId] = timer;
            })
            .addCase(fetchTimerByCard.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();
                timer.error = error;
                timer.hydrated = true;
                state.timers[cardId] = timer;
            })
            .addCase(deleteTimer.fulfilled, (state, action) => {
                const { cardId } = action.payload;
                delete state.timers[cardId];
            })
            .addCase(deleteTimer.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                const timer = state.timers[cardId] ?? createDefaultTimerState();
                timer.error = error;
                state.timers[cardId] = timer;
            })
            .addCase(fetchTimerBreakdown.pending, (state, action) => {
                const { cardId } = action.meta.arg;
                state.timerBreakdowns[cardId] = {
                    ...(state.timerBreakdowns[cardId] || {}),
                    loading: true,
                    error: null
                };
            })
            .addCase(fetchTimerBreakdown.fulfilled, (state, action) => {
                const { cardId, data } = action.payload;
                state.timerBreakdowns[cardId] = {
                    loading: false,
                    error: null,
                    days: data.days,
                    total: data.total,
                    formatted: data.formatted,
                    memberTotals: data.memberTotals
                };
            })
            .addCase(fetchTimerBreakdown.rejected, (state, action) => {
                const { cardId, error } = action.payload;
                state.timerBreakdowns[cardId] = {
                    loading: false,
                    error,
                    days: [],
                    total: 0,
                    formatted: '',
                    memberTotals: []
                };
            });
    },
});

export const {
    setTimer,
    setMode,
    setTimeLeft,
    setIsRunning,
    setTimerStatus,
    setTotalTime,
    setStartTime,
    setElapsedOffset,
    clearError,
    setError,
    // Timer Manager Actions
    showTimerManager,
    hideTimerManager,
    toggleTimerManager,
    collapseTimerManager,
    expandTimerManager,
    toggleTimerManagerCollapse,
    setTimerManagerPosition,
    setTimerManagerDragging,
    resetTimerManagerHiddenState,
} = timerSlice.actions;

export default timerSlice.reducer;
